import { collection, doc, getDoc, setDoc, updateDoc, deleteField, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { linkedInConfig, environment } from './config';

class LinkedInService {
  constructor() {
    this.clientId = linkedInConfig.clientId;
    
    // Brug det aktuelle domæne som redirect URI
    const hostname = window.location.hostname;
    if (hostname === 'test.app.nuvik.io') {
      this.redirectUri = 'https://test.app.nuvik.io';
    } else if (hostname === 'app.nuvik.io') {
      this.redirectUri = 'https://app.nuvik.io';
    } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
      this.redirectUri = 'http://localhost:3000';
    } else {
      // Fallback til environment variable
      this.redirectUri = process.env.REACT_APP_FRONTEND_URL || 'https://app.nuvik.io';
    }
    
    this.scope = linkedInConfig.scope;
    this.state = this._generateState();
    
    console.log(`LinkedInService initialiseret med redirectUri: ${this.redirectUri} og hostname: ${hostname}`);
  }

  // Generer en tilfældig streng til CSRF-beskyttelse
  _generateState() {
    const state = Math.random().toString(36).substring(2, 15);
    // Gem state i sessionStorage for senere validering
    try {
      sessionStorage.setItem('linkedin_state', state);
      console.log('State gemt i sessionStorage:', state);
    } catch (error) {
      console.error('Fejl ved gemning af state i sessionStorage:', error);
    }
    return state;
  }

  // Valider state-parameteren mod sessionStorage
  validateState(receivedState) {
    try {
      const storedState = sessionStorage.getItem('linkedin_state');
      console.log('Validerer state:', { receivedState, storedState });
      
      // Hvis vi ikke har en gemt state, returnerer vi true for at undgå
      // fejl ved første login eller i tilfælde af sessionstorage problemer
      if (!storedState) {
        console.warn('Ingen gemt state fundet i sessionStorage, springer validering over');
        return true;
      }
      
      return storedState === receivedState;
    } catch (error) {
      console.error('Fejl ved validering af state:', error);
      // Ved fejl i sessionStorage, tillad login for at undgå dårlig brugeroplevelse
      return true;
    }
  }

  // Omdirigér brugeren til LinkedIn for login
  redirectToLinkedIn() {
    // Generer en ny state
    this.state = this._generateState();
    
    // ÆNDRING: Sikrer at vi inkluderer w_member_social i scope
    this.scope = linkedInConfig.scope;
    if (!this.scope.includes('w_member_social')) {
      this.scope += ' w_member_social';
      console.log('Tilføjet w_member_social til scope:', this.scope);
    }
    
    // Byg auth URL
    const authUrl = `${linkedInConfig.authorizeEndpoint}?response_type=code&client_id=${this.clientId}&redirect_uri=${encodeURIComponent(this.redirectUri)}&state=${this.state}&scope=${encodeURIComponent(this.scope)}`;
    
    console.log(`Omdirigerer til LinkedIn med URL: ${authUrl}`);
    window.location.href = authUrl;
  }

  // Hent brugerens profil fra LinkedIn
  async getUserProfile(accessToken) {
    try {
      console.log('Henter LinkedIn profil med token:', accessToken.substring(0, 10) + '...');
      console.log('Bruger proxy endpoint i stedet for direkte API-kald for at omgå CORS');
      
      // Brug vores lokale proxy i stedet for at kalde LinkedIn direkte
      const response = await fetch('https://us-central1-app-nuvik.cloudfunctions.net/linkedInProfileProxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          access_token: accessToken
        })
      });

      if (!response.ok) {
        console.error('LinkedIn API fejl med status:', response.status);
        const errorText = await response.text();
        console.error('Fejlbesked:', errorText);
        throw new Error(`LinkedIn API fejl: ${response.status}`);
      }

      const data = await response.json();
      console.log('Hentet LinkedIn profil:', data);
      
      // Hent også member-ID for deling af opslag
      try {
        const memberResponse = await fetch('https://us-central1-app-nuvik.cloudfunctions.net/linkedInMemberProxy', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            access_token: accessToken
          })
        });
        
        if (memberResponse.ok) {
          const memberData = await memberResponse.json();
          console.log('Hentet LinkedIn member data:', memberData);
          // Tilføj member-ID til profil-data
          if (memberData && memberData.id) {
            data.memberId = memberData.id;
          }
        }
      } catch (memberError) {
        console.error('Fejl ved hentning af LinkedIn member-ID:', memberError);
        // Fortsæt selvom vi ikke kunne hente member-ID
      }
      
      return data;
    } catch (error) {
      console.error('Fejl ved hentning af LinkedIn profil:', error);
      
      // Returner en mock profil i tilfælde af fejl så vi stadig kan teste UI
      console.log('Returnerer mock profil som fallback');
      return {
        name: "LinkedIn Bruger",
        email: "bruger@example.com",
        sub: "mock-linkedin-id-" + Math.random().toString(36).substring(2, 10)
      };
    }
  }

  // Hent access token fra LinkedIn
  async getAccessToken(code) {
    try {
      console.log('Forsøger at hente token med kode:', code.substring(0, 10) + '...');
      console.log('Bruger proxy endpoint i stedet for direkte API-kald for at omgå CORS');
      console.log('Redirect URI:', this.redirectUri);
      
      const tokenResponse = await fetch('https://us-central1-app-nuvik.cloudfunctions.net/linkedInOAuthProxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: code,
          redirect_uri: this.redirectUri,
          client_id: this.clientId,
          client_secret: linkedInConfig.clientSecret
        })
      });

      if (!tokenResponse.ok) {
        const errorText = await tokenResponse.text();
        console.error('LinkedIn token endpoint fejl:', tokenResponse.status, errorText);
        throw new Error(`Token endpoint fejl: ${tokenResponse.status} - ${errorText}`);
      }

      const tokenData = await tokenResponse.json();
      console.log('Token hentet succesfuldt:', { 
        accessToken: tokenData.access_token ? (tokenData.access_token.substring(0, 10) + '...') : 'mangler', 
        expiresIn: tokenData.expires_in, 
        refreshToken: tokenData.refresh_token ? 'modtaget' : 'ikke tilgængelig' 
      });
      
      const result = {
        accessToken: tokenData.access_token,
        expiresIn: tokenData.expires_in
      };
      
      // Kun tilføj refreshToken hvis det findes
      if (tokenData.refresh_token) {
        result.refreshToken = tokenData.refresh_token;
      }
      
      return result;
    } catch (error) {
      console.error('Fejl ved hentning af access token:', error);
      throw error;
    }
  }

  // Gemmer LinkedIn-adgangstoken og profildata i Firestore
  async saveLinkedInToken(userId, tokenData, profileData) {
    if (!userId) {
      console.error('Ingen bruger-ID angivet for at gemme LinkedIn-token');
      return false;
    }

    try {
      console.log('Gemmer LinkedIn data for bruger:', userId);
      
      // Sikre at vi kun gemmer definerede værdier
      const safeTokenData = {...tokenData};
      
      // Fjern undefined værdier fra tokenData
      Object.keys(safeTokenData).forEach(key => {
        if (safeTokenData[key] === undefined) {
          console.log(`Fjerner undefined værdi for ${key}`);
          delete safeTokenData[key];
        }
      });
      
      console.log('Renset token data:', safeTokenData);
      console.log('Profil data:', profileData);
      
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      const dataToSave = {
        ...safeTokenData,
        profile: profileData,
        updatedAt: new Date().toISOString()
      };
      
      if (!userDoc.exists()) {
        console.log('Bruger dokument eksisterer ikke, opretter nyt...');
        await setDoc(userDocRef, {
          settings: {
            linkedin: dataToSave
          }
        });
      } else {
        console.log('Bruger dokument eksisterer, opdaterer LinkedIn indstillinger...');
        const settingsDocRef = doc(db, 'users', userId, 'settings', 'linkedin');
        await setDoc(settingsDocRef, dataToSave);
      }
      
      console.log('LinkedIn-token og profil gemt i Firestore');
      return true;
    } catch (error) {
      console.error('Fejl ved lagring af LinkedIn-token:', error);
      console.error('Fejldetaljer:', error.message);
      return false;
    }
  }

  // Henter LinkedIn-token og profil fra Firestore
  async getLinkedInToken(userId) {
    if (!userId) {
      console.error('Ingen bruger-ID angivet for at hente LinkedIn-token');
      return null;
    }

    try {
      console.log('Henter LinkedIn token for bruger:', userId);
      const tokenDoc = await getDoc(doc(db, 'users', userId, 'settings', 'linkedin'));
      
      if (tokenDoc.exists()) {
        const data = tokenDoc.data();
        console.log('Fundet LinkedIn data:', data);
        
        // Hvis dokumentet findes men ikke har meningsfuldt indhold,
        // tyder det på en ukomplet eller korrupt tilstand
        if (!data.accessToken && !data.profile) {
          console.log('Dokument findes, men mangler både accessToken og profile - betragtes som ingen forbindelse');
          return null;
        }
        
        return data;
      } else {
        console.log('Ingen LinkedIn-token fundet for brugeren');
        return null;
      }
    } catch (error) {
      console.error('Fejl ved hentning af LinkedIn-token:', error);
      return null;
    }
  }

  // Fjern LinkedIn-forbindelse for brugeren
  async disconnectLinkedIn(userId) {
    try {
      console.log(`Afkobler LinkedIn for bruger ${userId}...`);
      
      // Slet LinkedIn-indstillinger i Firestore fuldstændigt i stedet for at sætte felter til null
      await deleteDoc(doc(db, 'users', userId, 'settings', 'linkedin'));
      
      // Logfør handlingen
      console.log(`LinkedIn-forbindelse slettet for bruger ${userId}`);
      
      return true;
    } catch (error) {
      console.error('Fejl ved afkobling af LinkedIn:', error);
      throw error;
    }
  }

  // Metode til at sætte leveringspræference for AI-genererede opslag
  async setTelegramDeliveryPreference(userId, preference) {
    try {
      console.log(`Sætter leveringspræference til ${preference} for bruger ${userId}...`);
      
      if (preference !== 'website' && preference !== 'telegram') {
        throw new Error('Ugyldig præference. Skal være enten "website" eller "telegram"');
      }
      
      // Gem præferencen i Firestore
      await setDoc(doc(db, 'users', userId, 'settings', 'post_preferences'), {
        postDeliveryMethod: preference,
        updatedAt: new Date()
      }, { merge: true });
      
      return true;
    } catch (error) {
      console.error('Fejl ved indstilling af leveringspræference:', error);
      throw error;
    }
  }
}

export default new LinkedInService(); 